// LiqScoreWidget — compact horizontal pill for /liquidity-map.
// Lives between StatsBar and the predictor banner. Fetches
// `/api/liqmap/liqscore/{coin}` every 60s and renders score + breakdown.
//
// The widget is self-contained: no auth required, no shared state — drop
// it anywhere a `coin` prop is available.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";
import { useHardcodedI18n } from "../../i18n/hardcoded";
import { tCycles } from "../../i18n/cycles";
import { tDca, DCA_COINS } from "../../i18n/dca";

// Coins that have a halving and therefore expose a Cycles Map view.
// Kept in sync with HALVINGS in /app/backend/routes/liqmap/cycles.py.
const CYCLES_SUPPORTED_COINS = new Set(["BTC", "ZEC"]);
// Coins supported by the DCA simulator (BNB excluded — Kraken history <2y).
// Kept in sync with DCA_COINS in /app/frontend/src/i18n/dca.js.
const DCA_SUPPORTED_COINS = new Set(DCA_COINS);

const NIVELES_BY_LANG = {
  es: {
    inminente: { label: "CASCADA INMINENTE", emoji: "⚫", color: "#ffffff" },
    critico:   { label: "PRESIÓN CRÍTICA",   emoji: "🔴", color: "#ff3b5c" },
    alto:      { label: "PRESIÓN ALTA",      emoji: "🟠", color: "#ff5a00" },
    moderado:  { label: "PRESIÓN MODERADA",  emoji: "🟡", color: "#f0c800" },
    bajo:      { label: "PRESIÓN BAJA",      emoji: "🟢", color: "#00d084" },
  },
  en: {
    inminente: { label: "IMMINENT CASCADE",  emoji: "⚫", color: "#ffffff" },
    critico:   { label: "CRITICAL PRESSURE", emoji: "🔴", color: "#ff3b5c" },
    alto:      { label: "HIGH PRESSURE",     emoji: "🟠", color: "#ff5a00" },
    moderado:  { label: "MODERATE PRESSURE", emoji: "🟡", color: "#f0c800" },
    bajo:      { label: "LOW PRESSURE",      emoji: "🟢", color: "#00d084" },
  },
  sk: {
    inminente: { label: "BLÍŽIACA SA KASKÁDA", emoji: "⚫", color: "#ffffff" },
    critico:   { label: "KRITICKÝ TLAK",       emoji: "🔴", color: "#ff3b5c" },
    alto:      { label: "VYSOKÝ TLAK",         emoji: "🟠", color: "#ff5a00" },
    moderado:  { label: "MIERNY TLAK",         emoji: "🟡", color: "#f0c800" },
    bajo:      { label: "NÍZKY TLAK",          emoji: "🟢", color: "#00d084" },
  },
  de: {
    inminente: { label: "UNMITTELBARE KASKADE", emoji: "⚫", color: "#ffffff" },
    critico:   { label: "KRITISCHER DRUCK",     emoji: "🔴", color: "#ff3b5c" },
    alto:      { label: "HOHER DRUCK",          emoji: "🟠", color: "#ff5a00" },
    moderado:  { label: "MODERATER DRUCK",      emoji: "🟡", color: "#f0c800" },
    bajo:      { label: "GERINGER DRUCK",       emoji: "🟢", color: "#00d084" },
  },
  "zh-CN": {
    inminente: { label: "连环爆仓即将发生", emoji: "⚫", color: "#ffffff" },
    critico:   { label: "临界压力",         emoji: "🔴", color: "#ff3b5c" },
    alto:      { label: "高压力",           emoji: "🟠", color: "#ff5a00" },
    moderado:  { label: "中等压力",         emoji: "🟡", color: "#f0c800" },
    bajo:      { label: "低压力",           emoji: "🟢", color: "#00d084" },
  },
};

const BUTTON_TXT = {
  es: { simulate: "Simular liquidación", simulate_short: "SIMULAR", breakdown_aria: "Ver desglose", breakdown: "DESGLOSE", share: "🐦 COMPARTIR", shadow_aria: "Compartir captura del chart en Shadow Network", shadow_btn: "📸 SHADOW", history_empty: "acumulando histórico…" },
  en: { simulate: "Simulate liquidation", simulate_short: "SIMULATE", breakdown_aria: "View breakdown", breakdown: "BREAKDOWN", share: "🐦 SHARE", shadow_aria: "Share chart screenshot on Shadow Network", shadow_btn: "📸 SHADOW", history_empty: "building history…" },
  sk: { simulate: "Simulovať likvidáciu", simulate_short: "SIMULOVAŤ", breakdown_aria: "Zobraziť rozpis", breakdown: "ROZPIS", share: "🐦 ZDIEĽAŤ", shadow_aria: "Zdieľať snímku grafu na Shadow Network", shadow_btn: "📸 SHADOW", history_empty: "zbieram históriu…" },
  de: { simulate: "Liquidation simulieren", simulate_short: "SIMULIEREN", breakdown_aria: "Aufschlüsselung anzeigen", breakdown: "AUFSCHLÜSSELUNG", share: "🐦 TEILEN", shadow_aria: "Chart-Screenshot im Shadow Network teilen", shadow_btn: "📸 SHADOW", history_empty: "sammle Verlauf…" },
  "zh-CN": { simulate: "模拟强制平仓", simulate_short: "模拟", breakdown_aria: "查看明细", breakdown: "明细", share: "🐦 分享", shadow_aria: "在 Shadow Network 分享图表截图", shadow_btn: "📸 SHADOW", history_empty: "正在收集历史…" },
};

const NIVELES = NIVELES_BY_LANG.es;  // legacy alias — still used by scoreBand fallback

const scoreBand = (s, niveles = NIVELES_BY_LANG.es) => {
  if (s >= 90) return niveles.inminente;
  if (s >= 75) return niveles.critico;
  if (s >= 50) return niveles.alto;
  if (s >= 25) return niveles.moderado;
  return niveles.bajo;
};

const fmtSeconds = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m`);

// P2 — compact USD formatter for the transparency strip of the DESGLOSE.
// "$5,900,000" → "$5.9M", "$0" → "$0", "$1,250,000,000" → "$1.25B".
const fmtUsd = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n)}`;
};

// P2 — context strip for factors that currently score 0pts. Distinguishes
// "no data / no coverage" (defi_active=false, whales_active=false) from
// "value below threshold". `thresholds` is the sorted ascending list of
// USD thresholds (e.g. [10M, 100M, 500M] for DeFi). Returns plain text;
// the caller decides the styling.
const factorContextCopy = (value, active, thresholds) => {
  if (!active) return "sin cobertura aquí";
  const v = Number(value) || 0;
  if (!thresholds || thresholds.length === 0) return null;
  const next = thresholds.find((t) => v < t);
  if (next == null) return null;  // already past all thresholds, shouldn't be 0pts
  return `${fmtUsd(v)} (umbral ${fmtUsd(next)})`;
};

const buildTweetText = (coin, score, band, clusterMax, distPct, exchangesCount) => {
  const cmStr = clusterMax >= 1000
    ? `$${Math.round(clusterMax).toLocaleString("en-US")}`
    : `$${clusterMax.toFixed(2)}`;
  return (
    `⚡ RE₿EL LiqScore™ ${coin}: ${score}/100 ${band.emoji}\n\n` +
    `${band.label}\n` +
    `Cluster MÁX: ${cmStr}\n` +
    `Distancia: ${distPct.toFixed(2)}%\n` +
    `Basado en ${exchangesCount} exchanges + DeFi + Ballenas\n\n` +
    `🔍 rebelbtc.com/liquidity-map\n\n` +
    `#Bitcoin #${coin} #Crypto #Trading #Liquidaciones`
  );
};

export default function LiqScoreWidget({ coin = "BTC", onSimulateClick = null, onShareToShadow = null }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [tipOpen, setTipOpen] = useState(false);
  const tipRef = useRef(null);

  // Cycles-map navigation. Pre-selects the current coin via `?coin=`
  // when supported, falls back to BTC otherwise.
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const hc = useHardcodedI18n(lang);
  const tCy = tCycles(lang);
  const tDc = tDca(lang);
  const cyclesSupported = CYCLES_SUPPORTED_COINS.has(String(coin || "").toUpperCase());
  const dcaSupported = DCA_SUPPORTED_COINS.has(String(coin || "").toUpperCase());
  const onOpenCycles = () => {
    if (!cyclesSupported) return;
    navigate(`/liquidity-map/cycles?coin=${encodeURIComponent(String(coin).toUpperCase())}`);
  };
  const onOpenDca = () => {
    if (!dcaSupported) return;
    navigate(`/liquidity-map/dca?coin=${encodeURIComponent(String(coin).toUpperCase())}`);
  };

  // Fetch live score every 60s + history every 5 minutes.
  useEffect(() => {
    let alive = true;
    const loadScore = async () => {
      try {
        const r = await fetch(`${API}/liqmap/liqscore/${coin}`);
        const d = await r.json();
        if (alive) setData(d);
      } catch { /* silent */ }
    };
    const loadHistory = async () => {
      try {
        const r = await fetch(`${API}/liqmap/liqscore/${coin}/history?hours=6`);
        const d = await r.json();
        if (alive && Array.isArray(d.history)) setHistory(d.history);
      } catch { /* silent */ }
    };
    loadScore();
    loadHistory();
    const a = setInterval(loadScore, 60_000);
    const b = setInterval(loadHistory, 300_000);
    return () => { alive = false; clearInterval(a); clearInterval(b); };
  }, [coin]);

  // 1Hz tick for the "hace Xs" footer.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Close the breakdown tooltip on click-outside / Escape.
  useEffect(() => {
    if (!tipOpen) return undefined;
    const onDown = (e) => {
      if (tipRef.current && !tipRef.current.contains(e.target)) setTipOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setTipOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [tipOpen]);

  const score = Number(data?.score) || 0;
  const niveles = NIVELES_BY_LANG[lang] || NIVELES_BY_LANG.en || NIVELES_BY_LANG.es;
  const btnTxt = BUTTON_TXT[lang] || BUTTON_TXT.en || BUTTON_TXT.es;
  const band = useMemo(() => scoreBand(score, niveles), [score, niveles]);
  const fillPct = Math.max(0, Math.min(100, score));
  const ts = (data?.timestamp || 0) * 1000;
  const ago = ts ? Math.max(0, Math.floor((now - ts) / 1000)) : 0;
  const factores = data?.factores || {};

  const handleShare = () => {
    if (!data) return;
    const text = buildTweetText(
      coin,
      score,
      band,
      Number(data.cluster_max) || 0,
      Number(data.distancia_pct) || 0,
      Number(data.exchanges_count) || 0,
    );
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=560,height=520");
  };

  return (
    <div
      data-testid="liqscore-widget"
      className="relative my-1 flex flex-wrap items-center gap-3 rounded-lg px-3.5 py-2"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${band.color}55`,
        minHeight: 36,
      }}
    >
      {/* Brand badge */}
      <span
        className="font-mono font-bold uppercase"
        style={{ color: "#f97316", fontSize: 9, letterSpacing: "1px" }}
        data-testid="liqscore-brand"
      >
        ⚡ RE₿EL LiqScore™
      </span>
      <span className="h-4 w-px" style={{ background: "#222" }} />

      {/* Numeric score */}
      <span
        data-testid="liqscore-value"
        className="font-mono"
        style={{ fontSize: 16, fontWeight: 900, color: band.color, lineHeight: 1 }}
      >
        {data ? `${score}/100` : "…"}
      </span>

      {/* Progress bar */}
      <div
        data-testid="liqscore-bar"
        className="h-[6px] w-[80px] overflow-hidden rounded-[3px] sm:w-[120px]"
        style={{ background: "rgba(255,255,255,0.08)" }}
      >
        <div
          className="h-full transition-all duration-700 ease-out"
          style={{
            width: `${fillPct}%`,
            background: `linear-gradient(90deg, ${band.color}88, ${band.color})`,
          }}
        />
      </div>

      {/* Level label — with permanent disclaimer for the high-pressure
          bands (≥75). LiqScore is a live-pressure index (no verifier by
          design); the labels read as predictive, so the title attribute
          carries the honest meaning ("indice de condiciones actuales").
          The disclaimer chip below the bar is only rendered for high bands
          to keep the strip compact at low-pressure scores. */}
      <span
        className="font-mono font-bold uppercase"
        data-testid="liqscore-level"
        title={
          score >= 75
            ? "Índice de presión en tiempo real basado en 6 factores. No es una predicción verificada de que una cascada vaya a ocurrir."
            : `Nivel actual basado en 6 factores: ${band.label}.`
        }
        style={{ fontSize: 10, color: band.color, letterSpacing: "0.5px" }}
      >
        {band.emoji} {band.label}
      </span>
      {score >= 75 && (
        <span
          data-testid="liqscore-index-disclaimer"
          className="font-mono uppercase"
          style={{ fontSize: 8, color: `${band.color}cc`, letterSpacing: "0.5px" }}
          title="Índice de condiciones actuales — no es una predicción verificada."
        >
          · índice live
        </span>
      )}

      {/* Mini-history sparkline (last ~6h) — chronological order. Adapts
          gracefully to short histories (renders dots when ≤2 samples). */}
      {history.length > 0 && (
        <div
          className="flex items-center"
          data-testid="liqscore-history-sparkline"
          title={
            history.length === 1
              ? `1 muestra · ${history[0].score}/100`
              : `${history.length} muestras · ${history[0].score}/100 → ${history[history.length - 1].score}/100`
          }
        >
          {history.length < 3 ? (
            // Too few samples to draw a meaningful path — fall back to dots.
            <div className="flex items-center gap-1">
              {history.map((h, i) => {
                const b = scoreBand(h.score || 0);
                return (
                  <span
                    key={`${h.timestamp}-${i}`}
                    style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: b.color, display: "inline-block",
                    }}
                  />
                );
              })}
              <span className="ml-1 font-mono text-[8px] text-white/40">acumulando…</span>
            </div>
          ) : (
            (() => {
              const W = 72;
              const H = 18;
              const PAD = 1;
              const last = history.slice(-12);
              const path = last
                .map((h, i) => {
                  const x = PAD + (i / (last.length - 1)) * (W - 2 * PAD);
                  const y = H - PAD - ((Number(h.score) || 0) / 100) * (H - 2 * PAD);
                  return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
                })
                .join(" ");
              const lastScore = last[last.length - 1].score || 0;
              const lastBand = scoreBand(lastScore);
              return (
                <svg width={W} height={H} aria-hidden="true">
                  <path
                    d={path}
                    fill="none"
                    stroke={lastBand.color}
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    opacity="0.85"
                  />
                  <circle
                    cx={W - PAD}
                    cy={H - PAD - (lastScore / 100) * (H - 2 * PAD)}
                    r="1.8"
                    fill={lastBand.color}
                  />
                </svg>
              );
            })()
          )}
        </div>
      )}
      {history.length === 0 && data && (
        <span className="font-mono text-[8px] text-white/35" data-testid="liqscore-history-empty">
          {btnTxt.history_empty}
        </span>
      )}

      {/* Breakdown trigger + Twitter share */}
      <div className="ml-auto flex items-center gap-1.5">
        {onSimulateClick && (
          <button
            type="button"
            aria-label={btnTxt.simulate}
            data-testid="liqmap-sim-btn"
            title={btnTxt.simulate}
            onClick={onSimulateClick}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-[#f97316]/40 bg-[#f97316]/10 px-2 font-mono text-[10px] font-bold text-[#f97316] transition-all hover:bg-[#f97316]/20"
          >
            🎯 <span className="hidden sm:inline">{btnTxt.simulate_short}</span>
          </button>
        )}
        <button
          type="button"
          aria-label={btnTxt.breakdown_aria}
          data-testid="liqscore-breakdown-btn"
          onClick={() => setTipOpen((v) => !v)}
          className="rounded-md border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider transition-all hover:bg-white/[0.04]"
          style={{ borderColor: `${band.color}55`, color: band.color }}
        >
          {btnTxt.breakdown}
        </button>
        <button
          type="button"
          aria-label={hc.share_on_x}
          data-testid="liqscore-share-btn"
          onClick={handleShare}
          className="rounded-md border border-white/15 bg-black px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider text-white transition-all hover:bg-white hover:text-black"
        >
          {btnTxt.share}
        </button>
        {onShareToShadow && (
          <button
            type="button"
            aria-label={btnTxt.shadow_aria}
            data-testid="liqmap-share-to-shadow"
            onClick={onShareToShadow}
            className="rounded-md border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider transition-all hover:bg-cyan-400/10"
            style={{ borderColor: "rgba(34,211,238,0.55)", color: "#22d3ee" }}
          >
            {btnTxt.shadow_btn}
          </button>
        )}
        {/* Cycles map — link to /liquidity-map/cycles, pre-selecting the
            current coin when supported (BTC/ZEC). Disabled for coins
            without a halving — same UX pattern as the in-page coin
            selector inside CyclesMapView. NOT added to the chart-toolbar
            because that row was reorganised into categories due to
            space constraints in a prior phase; this row has slack. */}
        <button
          type="button"
          aria-label={cyclesSupported ? tCy.entry_button_title : tCy.entry_disabled_tooltip}
          data-testid="liqmap-cycles-entry-btn"
          title={cyclesSupported ? tCy.entry_button_title : tCy.entry_disabled_tooltip}
          onClick={onOpenCycles}
          disabled={!cyclesSupported}
          className={`rounded-md border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider transition-all ${
            cyclesSupported
              ? "border-amber-400/55 text-amber-300 hover:bg-amber-400/10"
              : "cursor-not-allowed border-white/10 text-white/30"
          }`}
        >
          {tCy.entry_button_label}
        </button>
        {/* DCA simulator entry — same UX pattern as Cycles. Disabled
            for BNB (Kraken history <2y) with the explicit tooltip from
            i18n/dca.js so the affordance is visible. */}
        <button
          type="button"
          aria-label={dcaSupported ? tDc.entry_button_title : tDc.bnb_disabled_tooltip}
          data-testid="liqmap-dca-entry-btn"
          title={dcaSupported ? tDc.entry_button_title : tDc.bnb_disabled_tooltip}
          onClick={onOpenDca}
          disabled={!dcaSupported}
          className={`rounded-md border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider transition-all ${
            dcaSupported
              ? "border-emerald-400/55 text-emerald-300 hover:bg-emerald-400/10"
              : "cursor-not-allowed border-white/10 text-white/30"
          }`}
        >
          {tDc.entry_button_label}
        </button>
        <span
          className="font-mono text-white/30"
          style={{ fontSize: 8 }}
          data-testid="liqscore-ago"
        >
          hace {fmtSeconds(ago)}
        </span>
      </div>

      {/* Breakdown tooltip — anchored top-right */}
      {tipOpen && (
        <div
          ref={tipRef}
          data-testid="liqscore-breakdown"
          className="absolute right-2 top-full z-40 mt-1 w-[260px] rounded-lg border p-3 font-mono text-[10.5px] leading-snug shadow-xl"
          style={{
            background: "rgba(8,5,8,0.97)",
            borderColor: `${band.color}40`,
            color: "#ddd",
            backdropFilter: "blur(10px)",
          }}
        >
          <p className="mb-1.5 text-[9px] uppercase tracking-[0.18em] text-white/55">Desglose · {score}/100</p>
          <ul className="space-y-1">
            <li className="flex justify-between"><span>Distancia cluster</span><span className="font-bold text-white">+{factores.distancia_cluster?.puntos ?? 0} pts</span></li>
            <li className="flex justify-between"><span>Intensidad</span><span className="font-bold text-white">+{factores.intensidad_cluster?.puntos ?? 0} pts</span></li>
            <li className="flex justify-between"><span>Ratio L/S</span><span className="font-bold text-white">+{factores.ratio_liq?.puntos ?? 0} pts</span></li>
            <li className="flex justify-between"><span>Velocidad OI</span><span className="font-bold text-white">+{factores.velocidad_oi?.puntos ?? 0} pts</span></li>
            {/* DeFi zona — transparency strip when 0pts (P2 Tarea 1). */}
            <li className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5" data-testid="liqscore-breakdown-defi">
              <span>DeFi zona</span>
              <span className="font-bold text-white">+{factores.defi_en_zona?.puntos ?? 0} pts</span>
              {(factores.defi_en_zona?.puntos ?? 0) === 0 && (
                <span
                  className="basis-full text-[9px] text-white/45"
                  data-testid="liqscore-breakdown-defi-ctx"
                >
                  {factorContextCopy(
                    factores.defi_en_zona?.valor,
                    data?.defi_active,
                    data?.umbrales?.defi_en_zona,
                  ) || "—"}
                </span>
              )}
            </li>
            {/* Ballenas zona — same pattern. */}
            <li className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5" data-testid="liqscore-breakdown-whales">
              <span>Ballenas zona</span>
              <span className="font-bold text-white">+{factores.ballenas_en_zona?.puntos ?? 0} pts</span>
              {(factores.ballenas_en_zona?.puntos ?? 0) === 0 && (
                <span
                  className="basis-full text-[9px] text-white/45"
                  data-testid="liqscore-breakdown-whales-ctx"
                >
                  {factorContextCopy(
                    factores.ballenas_en_zona?.valor,
                    data?.whales_active,
                    data?.umbrales?.ballenas_en_zona,
                  ) || "—"}
                </span>
              )}
            </li>
          </ul>
          <p className="mt-2 italic text-white/40" style={{ fontSize: 9 }}>
            Índice de presión live · {data?.exchanges_count || 0} exchanges + DeFi + Ballenas · recalculado cada 60s
          </p>
        </div>
      )}
    </div>
  );
}
